import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import apiClient from "@/app/api/client";
import { getErrorMessage } from "@/shared/utils/getErrorMessage";
import type { LeadFormInput } from "../services/leadForms.service";
import { leadFormsService } from "../services/leadForms.service";
import type { FormSchema, LeadFormAssignmentKind } from "../types";

function useLeadFormsMutation<TVariables, TData>(
	mutationFn: (variables: TVariables) => Promise<TData>,
	queryKey: unknown[],
	successMessage: string,
	errorFallback: string
) {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey });
			message.success(successMessage);
		},
		onError: (error) => {
			// `messages.leadForms` aniq sabab beradi: nom band, forma
			// arxivlangan, sxema noto'g'ri, nishon topilmadi.
			message.error(getErrorMessage(error, errorFallback));
		},
	});
}

export function useLeadFormsList(isActive: boolean) {
	return useQuery({
		queryKey: ["lead-forms", { isActive }],
		queryFn: () => leadFormsService.list(isActive),
	});
}

export function useLeadFormDetail(id: string | null) {
	return useQuery({
		queryKey: ["lead-forms", id],
		queryFn: () => leadFormsService.get(id as string),
		enabled: id !== null,
	});
}

/**
 * Versiya sxemasi — **o'zgarmas**, shuning uchun cheksiz `staleTime`.
 * Qayta so'rash ma'nosiz: nashr etilgan versiya hech qachon o'zgarmaydi.
 */
export function useLeadFormVersion(id: string | null, version: number | null) {
	return useQuery({
		queryKey: ["lead-forms", id, "versions", version],
		queryFn: () => leadFormsService.getVersion(id as string, version as number),
		enabled: id !== null && version !== null,
		staleTime: Number.POSITIVE_INFINITY,
	});
}

export function useCreateLeadForm() {
	return useLeadFormsMutation(
		(input: LeadFormInput) => leadFormsService.create(input),
		["lead-forms"],
		"Forma yaratildi",
		"Formani yaratib bo'lmadi"
	);
}

export function useUpdateLeadForm() {
	return useLeadFormsMutation(
		({ id, input }: { id: string; input: Partial<LeadFormInput> }) =>
			leadFormsService.update(id, input),
		["lead-forms"],
		"Forma yangilandi",
		"Formani yangilab bo'lmadi"
	);
}

export function useArchiveLeadForm() {
	return useLeadFormsMutation(
		(id: string) => leadFormsService.archive(id),
		["lead-forms"],
		"Forma arxivlandi",
		"Formani arxivlab bo'lmadi"
	);
}

export function usePublishLeadFormVersion(formId: string | null) {
	return useLeadFormsMutation(
		(schema: FormSchema) => leadFormsService.publish(formId as string, schema),
		["lead-forms"],
		"Yangi versiya nashr etildi",
		"Versiyani nashr etib bo'lmadi"
	);
}

export function useLeadFormAssignments(id: string | null) {
	return useQuery({
		queryKey: ["lead-forms", id, "assignments"],
		queryFn: () => leadFormsService.listAssignments(id as string),
		enabled: id !== null,
	});
}

export function useReplaceLeadFormAssignments(id: string | null) {
	return useLeadFormsMutation(
		(assignments: { kind: LeadFormAssignmentKind; targetId: string }[]) =>
			leadFormsService.replaceAssignments(id as string, assignments),
		["lead-forms", id, "assignments"],
		"Biriktirishlar saqlandi",
		"Biriktirishlarni saqlab bo'lmadi"
	);
}

interface NamedOption {
	id: string;
	name: string;
}

/**
 * Biriktirish nishonlari: xodimlar (server tomonida qidiriladi),
 * bo'limlar va lavozimlar (tekis ro'yxat). Uchalasi bitta hookda —
 * drawer ularni birga ishlatadi va uchta alohida hook chaqiruvi
 * komponentni shovqinli qilardi.
 *
 * Kesh kalitlari `useSchedules.ts` dagilar bilan ATAYLAB bir xil
 * (`["departments","all"]`, `["positions","all"]`) — ikki modul bitta
 * javobni bo'lishadi.
 *
 * ⚠️ **Ruxsat qarzi:** bu uch so'rov `employees:read`, `departments:read`
 * va `positions:read` ni talab qiladi. Faqat `lead-forms:*` berilgan
 * foydalanuvchi 403 oladi va tanlagichlar SABABSIZ bo'sh chiqadi. Rol
 * urug'lashda buni hisobga oling; drawer'ni ishlatadigan rolga uch
 * o'qish ruxsati ham berilishi kerak.
 */
export function useAssignmentTargetOptions(employeeSearch: string) {
	const employees = useQuery({
		queryKey: ["employees", "lead-form-options", employeeSearch],
		queryFn: async () => {
			const params: Record<string, string | number> = { page: 1, limit: 100 };

			if (employeeSearch.trim()) {
				params.q = employeeSearch.trim();
			}

			const response = await apiClient.get<{
				success: true;
				data: { items: { id: string; fullName: string }[] };
			}>("/employees", { params });

			return response.data.data.items.map((item) => ({ id: item.id, name: item.fullName }));
		},
		staleTime: 30_000,
	});

	const departments = useQuery({
		queryKey: ["departments", "all"],
		queryFn: async () => {
			const response = await apiClient.get<{ success: true; data: NamedOption[] }>("/departments");
			return response.data.data;
		},
		staleTime: 60_000,
	});

	const positions = useQuery({
		queryKey: ["positions", "all"],
		queryFn: async () => {
			const response = await apiClient.get<{ success: true; data: NamedOption[] }>("/positions");
			return response.data.data;
		},
		staleTime: 60_000,
	});

	return {
		employee: employees.data ?? [],
		department: departments.data ?? [],
		position: positions.data ?? [],
		isLoading: employees.isLoading || departments.isLoading || positions.isLoading,
	};
}
